<template>
<div
	class="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-2 ps-3 pb-2 mb-1 border-bottom">
	<h4 class="fs-4 m-0">Адміністрування користувачів</h4>

	<span class="d-inline-block" tabindex="0" data-bs-toggle="tooltip" data-bs-title="додати користувача">
		<button
			type="button"
			class="btn btn-outline-secondary me-4"
			@click="createNewUser"
		>
			<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-person-plus" viewBox="0 0 16 16">
				<path d="M6 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H1s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C9.516 10.68 8.289 10 6 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"></path>
				<path fill-rule="evenodd" d="M13.5 5a.5.5 0 0 1 .5.5V7h1.5a.5.5 0 0 1 0 1H14v1.5a.5.5 0 0 1-1 0V8h-1.5a.5.5 0 0 1 0-1H13V5.5a.5.5 0 0 1 .5-.5z"></path>
			</svg>
		</button>
	</span>
</div>

<easy-data-table
	class="data-table m-1 mt-0 me-1"
	table-class-name="customize-table"
	:headers="tableHeader"
	:items="tableItems"
	:loading="isLoading"
	theme-color="rgb(198, 199, 200)"
	must-sort="true"
	:sort-by="sorting.sortBy"
	:sort-type="sorting.sortType" 
	:rows-items="[40,100,200,400,1000]"
	:rows-per-page="rowsPerPage"
	:key="rowsPerPage"
	rows-per-page-message="записів на сторінці"
	rows-of-page-separator-message=" з "
	:table-min-height="380"
	:table-height="easyDataTableHeight()"
	:prevent-context-menu-row="false"
	alternating
	buttons-pagination
	no-hover
>
	<!-- If rows is empty -->
	<template #empty-message>
		Користувачів не знайдено
	</template>

	<template #loading>
		<div class="spinner-border" style="width: 3rem; height: 3rem;" role="status">
			<span class="visually-hidden">Завантаження...</span>
		</div>
	</template>

	<!-- Login item customization -->
	<template #[`item-${accountStore.schema.login}`]="item">
		<button
			type="button"
			class="btn text-nowrap text-start w-100 h-100 m-0 p-0"
			style="font-family: inherit; font-size: inherit;"
			@click.stop="editUser(item['_id'])"
		>
			<svg
				v-if="!item[accountStore.schema.isEnabled]"
				xmlns="http://www.w3.org/2000/svg"
				width="16"
				height="16"
				fill="currentColor"
				class="bi bi-person-dash"
				viewBox="0 0 16 16"
			>
				<path d="M12.5 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7M11 12h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1 0-1m0-7a3 3 0 1 1-6 0 3 3 0 0 1 6 0M8 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4"/>
				<path d="M8.256 14a4.5 4.5 0 0 1-.229-1.004H3c.001-.246.154-.986.832-1.664C4.484 10.68 5.711 10 8 10q.39 0 .74.025c.226-.341.496-.65.804-.918Q8.844 9.002 8 9c-5 0-6 3-6 4s1 1 1 1z"/>
			</svg>
			{{ item[accountStore.schema.login] }}
			{{ item[accountStore.schema.isEnabled] ? '' : '(вимкнений)' }}
		</button>
	</template>

	<!-- Name item customization -->
	<template #[`item-${accountStore.schema.name}`]="item">
		<button
			type="button"
			class="btn text-nowrap text-start w-100 h-100 m-0 p-0"
			style="font-family: inherit; font-size: inherit;"
			@click.stop="editUser(item['_id'])"
		>
			{{ item[accountStore.schema.name] }}
		</button>
	</template>

	<!-- Role item customization -->
	<template #[`item-${accountStore.schema.role}`]="item">
		{{ accountSchema.role.allowed[item[accountSchema.role.key]]?.title ?? 'невідомо' }}
	</template>
	
	<!-- Last activity item customization -->
	<template #[`item-${sessionStore.sessionSchema.lastActivity}`]="item">
		{{ lastActivityByUserId[item[accountSchema.id.key]] ? activityPromt(lastActivityByUserId[item[accountSchema.id.key]]) : '-' }}
	</template>

	<!-- Actions item customization -->
	<template #item-actions="{ _id }">
		<!-- {{ console.log(_id) }} -->
		<button
			type="button"
			class="btn btn-sm btn-outline-secondary table-action-button"
			@click.stop="editUser(_id)"
		>
			<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-pencil" viewBox="0 0 16 16">
				<path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325"/>
			</svg>
		</button>
		&nbsp;
		<button
			v-if="this.accountStore.isPrimary"
			type="button"
			class="btn btn-sm btn-outline-secondary table-action-button"
			@click.stop="userSessions(_id)"
		>
			<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-calendar-week" viewBox="0 0 16 16">
				<path d="M11 6.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm-3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm-5 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5z"/>
				<path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5M1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4z"/>
			</svg>
		</button>
	</template>
</easy-data-table>
</template>
    
    
<script>
import { useTooltip } from '@/composables/bootstrap-tooltip.js'
import { useAccontStore } from '@/stores/AccountStore.js'
import { useSessionStore } from '@/stores/SessionStore.js'
import { useAccounts } from '@/composables/accounts.js'
import {
	easyDataTableHeight,
	easyDataTableRowsPerPage,
	dateTimeToString,
	capitalizeFirstLetter,
} from '@/helpers.js'

	
export default {
	inject: ['developerId'],
	
	components: {},

	props: {},

	setup() {
		const accountStore = useAccontStore()
		const sessionStore = useSessionStore()
		const {
			accountSchema,
			allAccounts,
			allAccountsFiltered,
			isLoading,
		} = useAccounts()
		
		useTooltip()

		return {
			easyDataTableHeight,
			easyDataTableRowsPerPage,
			dateTimeToString,

			accountStore,
			sessionStore,

			accountSchema,
			allAccounts,
			allAccountsFiltered,
			isLoading
		}
	},

	data() {
		return {
			// Table
			rowsPerPage: 20,
			lastActivityByUserId: {},
		}
	},

	watch: {
		accounts: {
			immediate: true,
			async handler(newAccounts) {
				for (const account of newAccounts) {
					const userId = account[this.accountSchema.id.key]
					// For each account. Load last activity time
					this.lastActivityByUserId[userId] = await this.sessionStore.getUserLastActivity(userId)
				}
			}
		}
	},

	created() {
	},

	mounted() {
		document.title = "Користувачі"
		this.rowsPerPage = easyDataTableRowsPerPage()
	},

	methods: {
		activityPromt(time) {
			try {
				let promt = ''
				const offset = 0//time.getTimezoneOffset() * 60000 // timezone offset
				const lastActivityS = (new Date().getTime() - time.getTime() + offset) / 1000

				if (lastActivityS < 60) {
					promt = "< хв"
				} else if (lastActivityS >= 60 && lastActivityS <= 3600) {
					promt = `${Math.floor(lastActivityS/60)} хв`
				} else if (lastActivityS > 3600 && lastActivityS <= 86400) {
					promt = `${Math.floor(lastActivityS/3600)} год`
				} else if (lastActivityS > 86400 && lastActivityS <= 2592000) {
					promt = `${Math.floor(lastActivityS/86400)} днів`
				} else if (lastActivityS > 2592000) {
					promt = "> 30 днів"
				}

				promt += ` (${dateTimeToString(time, false)})`

				return promt
			} catch (error) {
				return "-"
			}
		},

		editUser(userId) {
			const id = String(userId)
			this.$router.push({ name: 'user', params: { id } })
			//return userId
		},

		createNewUser() {
			const id = 'new'
			this.$router.push({ name: 'user', params: { id } })
		},

		userSessions(userId) {
			const id = String(userId)
			this.$router.push({ name: 'user_sessions', params: { id } })
		},
	},

	computed: {
		accounts() {
			return this.accountStore.isPrimary
				? this.allAccountsFiltered()
				: this.allAccountsFiltered({
					organization: this.accountStore.organization[this.accountSchema.id.key],
				})
		},

		tableHeader() {
			const header = [
				{
					text: capitalizeFirstLetter(this.accountSchema.login.title),
					value: this.accountStore.schema.login,
					sortable: true,
				},
				{
					text: capitalizeFirstLetter(this.accountSchema.name.title),
					value: this.accountStore.schema.name,
					sortable: true,
				},
				
			]

			if (this.accountStore.isPrimary) {
				header.push({
					text: capitalizeFirstLetter(this.accountSchema.organization.title),
					value: `${this.accountStore.schema.organization.self}.${this.accountStore.schema.organization.title}`,
					sortable: true,
				})
			}

			header.push({ text: capitalizeFirstLetter(this.accountSchema.role.title), value: this.accountStore.schema.role, })
			header.push({ text: "Активність", value: this.sessionStore.sessionSchema.lastActivity, })
			header.push({ text: "Дії", value: 'actions',  width: 100, })

			return header
		},

		tableItems() {
			return this.accountStore.userId == this.developerId ? this.accounts : this.filteredUsers
		},

		filteredUsers() {
			// Filtering function
			const filter_func = function (user) {
				if (user.login == 'system' || user.login == 'scraper') {
					return false
				}

				return true
			}

			return this.accounts.filter(filter_func, this);
		},

		sorting() {
			if (this.accountStore.isPrimary) {
				return {
					sortBy: `${this.accountStore.schema.organization.self}.${this.accountStore.schema.organization.title}`,
					sortType: 'asc',
				}
			} else {
				return {
					sortBy: this.accountStore.schema.login,
					sortType: 'asc',
				}
			}
		},
	},
}
</script>
    
    

<style src="@/assets/table.css" scoped />
<style scoped>
.disabled-user {
	opacity: 0,8 !important;
	color: aqua !important;
}
</style>
